import React, { useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Toaster } from "react-hot-toast";
import useEPaper from "../useEPaper";
import Loader from "../../../components/Loader";
import CommonDataGrid from "../../../components/CommonDataGrid";
import Button from "../../../components/Botton";
import EditImg from "../../../images/SideBar/Edit";
import DeleteImg from "../../../images/SideBar/Delete";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { hasPermission } from "../../../utils/permissions";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/slices/authSlice";
import { getDeleteConfirmationMessage } from "../../../utils/pagePermissions";

const EPaper: React.FC = () => {
  const user = useAppSelector(selectUser);
  const canEdit = hasPermission(user, "e-paper", "update");
  const canAdd = hasPermission(user, "e-paper", "create");
  const canDelete = hasPermission(user, "e-paper", "delete");

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date",
      align: "center",
      headerAlign: "center",
      flex: 1,
      type: "date",
      valueGetter: (value: any, row: any) => {
        if (row?.date) {
          return new Date(row.date);
        }
        return value ? new Date(value) : null;
      },
      renderCell: (params: any) => {
        if (params.row?.date) {
          return new Date(params.row.date).toLocaleDateString();
        }
        return params.value ? new Date(params.value).toLocaleDateString() : "—";
      },
    },
    {
      field: "pdfUrl",
      headerName: "PDF",
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <button
            onClick={() => {
              let url = params.value;
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
            type="button"
          >
            View PDF
          </button>
        ) : (
          "—"
        ),
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: "Actions",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="flex h-full items-center justify-center gap-3">
          {canEdit && (
            <button
              onClick={() => handleEdit(params.row._id)}
              className="text-fontColor hover:text-primary-2"
              title="Edit"
            >
              <EditImg />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => openDeleteModal(params.row._id)}
              className="text-fontColor hover:text-red-500"
              title="Delete"
            >
              <DeleteImg />
            </button>
          )}
        </div>
      ),
    });
  }

  const {
    ePaperData,
    loading,
    handleEdit,
    handleDelete,
    handleAdd,
    setFilterModel,
    setSortingModel,
    rowCount,
    paginationModel,
    setPaginationModel,
  } = useEPaper(columns);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ePaperToDelete, setEPaperToDelete] = useState<string | null>(null);

  const openDeleteModal = (id: string) => {
    setEPaperToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (ePaperToDelete) {
      handleDelete(ePaperToDelete);
      setIsModalOpen(false);
    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setEPaperToDelete(null);
  };

  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}

      <div className="space-y-4 mt-4 bg-white p-4 rounded-b-lg shadow-md">
        <div className="flex justify-end items-center gap-4 mr-1">
          {canAdd && (
            <Button
              onClick={handleAdd}
              className="py-1.5 px-9 rounded"
              children="Add E-paper"
              type="button"
            />
          )}
        </div>

        <CommonDataGrid
          id="epaper"
          rows={ePaperData}
          columns={columns}
          loading={loading}
          showConfirmation={false}
          confirmationMessage={""}
          setFilterModel={setFilterModel}
          setSortingModel={setSortingModel}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onCancelDelete={() => {}}
          onConfirmDelete={() => {}}
          toolbarProps={{ toolbar: { showQuickFilter: true } }}
        />
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message={getDeleteConfirmationMessage("epaper")}
      />
      <Toaster />
    </div>
  );
};

export default EPaper;
